import React, { useState, useEffect } from 'react';
import Receipt from './Receipt';

const STATUS_FLOW = [
  { value: 'Registration Done', label: 'Registration Done / नोंदणी पूर्ण झाली' },
  { value: 'Card Dispatched to Vendor', label: 'Card Dispatched to Vendor / कार्ड विक्रेत्याकडे पाठवले' },
  { value: 'Card Dispatched to DC', label: 'Card Dispatched to DC / कार्ड जिल्हा समन्वयाकाकडे पाठवले' },
  { value: 'Card Dispatched to TCC', label: 'Card Dispatched to TCC / कार्ड टीसीसीकडे पाठवले' },
  { value: 'Card Dispatched to Survivor', label: 'Card Dispatched to Survivor / कार्ड लाभार्थ्याकडे पाठवले' },
  { value: 'Card Delivered', label: 'Card Delivered / कार्ड वितरित झाले' }
];

const getStatusClass = (status) => {
  if (!status) return 'status-registration-done';
  return 'status-' + status.toLowerCase().replace(/\s+/g, '-');
};

function Dashboard({ onNewRegistration, onLogout, user, API }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [updatingAadhaar, setUpdatingAadhaar] = useState(null);

  useEffect(() => {
    fetch(`${API}/registration/list`, { credentials: 'include' })
      .then((res) => res.json())
      .then((json) => {
        if (json.error) {
          setError(json.error);
        } else {
          setData(json);
        }
      })
      .catch(() => setError('Failed to load registrations'))
      .finally(() => setLoading(false));
  }, [API]);

  const handleStatusChange = async (aadhaarNumber, newStatus) => {
    setUpdatingAadhaar(aadhaarNumber);
    try {
      const res = await fetch(`${API}/registration/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ aadhaarNumber, status: newStatus }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json.error || 'Failed to update status');
      } else {
        setData((prevData) => {
          if (!prevData || !prevData.registrations) return prevData;
          return {
            ...prevData,
            registrations: prevData.registrations.map((reg) =>
              reg.aadhaarNumber === aadhaarNumber ? { ...reg, status: newStatus } : reg
            ),
          };
        });
      }
    } catch (err) {
      alert('Error updating status. Please try again.');
    } finally {
      setUpdatingAadhaar(null);
    }
  };

  if (selectedReceipt) {
    return (
      <Receipt
        data={selectedReceipt}
        onNewRegistration={() => setSelectedReceipt(null)}
        onLogout={onLogout}
        onGoToDashboard={() => setSelectedReceipt(null)}
      />
    );
  }

  return (
    <div className="dashboard-card">
      <h2>Welcome, Surveyor / स्वागत आहे, सर्वेक्षक</h2>
      <p>
        Logged in as / म्हणून लॉग इन केले: <strong>{user?.email || user?.contact}</strong>
      </p>

      {loading && <p className="loading-text">Loading registrations...</p>}
      {error && <p className="error-msg">{error}</p>}

      {data && (
        <div className="dashboard-stats">
          <div className="stat-box">
            <span className="stat-number">{data.count}</span>
            <span className="stat-label">Total Registrations / एकूण नोंदणी</span>
          </div>
        </div>
      )}

      <button className="btn-dashboard" onClick={onNewRegistration}>
        + New Registration / नवीन नोंदणी
      </button>

      {data && data.registrations && data.registrations.length > 0 && (
        <div className="registrations-table-wrapper">
          <h3>Your Registrations / तुमच्या नोंदणी</h3>
          <div className="table-scroll">
            <table className="registrations-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Timestamp / वेळ</th>
                  <th>Kutumb Kramank / कुटुंब क्र.</th>
                  <th>Sadasya Kramank / सदस्य क्र.</th>
                  <th>Kamgar Name / कामगार नाव</th>
                  <th>District / जिल्हा</th>
                  <th>Taluka / तालुका</th>
                  <th>Village / गाव</th>
                  <th>Mobile / मोबाईल</th>
                  <th>Aadhaar / आधार</th>
                  <th>Arj Kramank / अर्ज क्र.</th>
                  <th>Kamgar Prakar / कामगार प्रकार</th>
                  <th>Anubhavi Varsh / अनुभवी वर्ष</th>
                  <th>Status / स्थिती</th>
                  <th>Action / क्रिया</th>
                </tr>
              </thead>
              <tbody>
                {data.registrations.map((reg, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{reg.timestamp}</td>
                    <td>{reg.kutumbKramank}</td>
                    <td>{reg.sadasyaKramank}</td>
                    <td>{reg.kamgarName}</td>
                    <td>{reg.district}</td>
                    <td>{reg.taluka}</td>
                    <td>{reg.village}</td>
                    <td>{reg.mobile}</td>
                    <td>{reg.aadhaarNumber}</td>
                    <td>{reg.arjKramank}</td>
                    <td>{reg.kamgarPrakar}</td>
                    <td>{reg.anubhaviVarsh}</td>
                    <td className="status-cell">
                      <select
                        value={reg.status || 'Registration Done'}
                        onChange={(e) => handleStatusChange(reg.aadhaarNumber, e.target.value)}
                        disabled={updatingAadhaar === reg.aadhaarNumber}
                        className={`status-select-inline ${getStatusClass(reg.status)}`}
                      >
                        {STATUS_FLOW.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {updatingAadhaar === reg.aadhaarNumber && (
                        <div className="status-loading-spinner"></div>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-print-receipt"
                        onClick={() => setSelectedReceipt({
                          ...reg,
                          surveyorContact: user?.contact || '',
                          surveyorEmail: user?.email || '',
                        })}
                      >
                        Print Receipt
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
